import { Injectable } from '@angular/core';
import CryptoJS from 'crypto-js';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { EncryptionInterface } from '../interfaces/encryption.interface';
import { compressToUint8Array, decompressFromUint8Array } from 'lz-string';

@Injectable({
    providedIn: 'root',
})
export class EncryptionService implements EncryptionInterface {
    public static readonly PREFIX_ENCRYPTED_CONTENT = '[[-AES-]]';
    private static readonly PREFIX_ENCRYPTED_KEY = '[[-AESENCKEY-]]';
    private encryptionKey: string;
    private readonly log = console;
    private appId = 'EncryptionService';
    private initialized = new BehaviorSubject<boolean>(false);

    public init(encryptionKey: string, appId: string): Observable<void> {
        if (encryptionKey.length === 0) {
            throw new Error(`EncryptionService: init: No encryption keys found for the user`);
        }
        this.appId = appId;
        return this.setEncryptionKey(encryptionKey).pipe(
            tap(() => {
                this.initialized.next(this.encryptionKey?.startsWith(EncryptionService.PREFIX_ENCRYPTED_KEY));
            }),
        );
    }

    public get isInitialized(): boolean {
        return this.initialized.getValue();
    }

    public get selectInitialized(): Observable<boolean> {
        return this.initialized.asObservable();
    }

    private setEncryptionKey(encryptedKey: string): Observable<void> {
        const encryptedPassphrase = encryptedKey;
        if (encryptedPassphrase) {
            // The passphrase from GW is encrypted. We need to decrypt it in order to use it.
            this.encryptionKey = CryptoJS.AES.decrypt(encryptedPassphrase, this.appId).toString(CryptoJS.enc.Utf8);
            if (!this.encryptionKey || !this.encryptionKey.startsWith(EncryptionService.PREFIX_ENCRYPTED_KEY)) {
                // The encryption of the passphrase did not work.
                // Without a passphrase any encryption or decryption will not work.
                this.log.error(`EncryptionService: setEncryptionKey: The decryption of the passphrase did not work!`);
            }
        } else {
            // Without a passphrase any encryption or decryption will not work.
            this.log.error(
                `EncryptionService: setEncryptionKey: Without a passphrase any encryption or decryption will not work!`,
            );
            throw new Error(
                `EncryptionService: setEncryptionKey: Without a passphrase any encryption or decryption will not work!`,
            );
        }
        return of(undefined);
    }

    /**
     * Checks for valid parameters then and encrypts and prefixes it with AES and the private key.
     * Will throw errors if the provided parameters are illegal.
     *
     * @param data the data to be encrypted
     * @returns encrypted data as string
     * @throws Error if the service is not yet initialised or the provided data was empty
     */
    public encrypt(data: string): string {
        if (!this.isInitialized) {
            throw new Error(`EncryptionService: encrypt: Trying to call EncryptionService before initialization!`);
        }

        if (!data || data.length < 1) {
            this.log.error('EncryptionService: encrypt: The provided data was empty. Empty data cannot be encrypted.');
            throw new Error('EncryptionService: encrypt: The provided data was empty. Empty data cannot be encrypted.');
        }

        const encrypted = CryptoJS.AES.encrypt(data, this.encryptionKey).toString();

        // prefix the data to be able to recognize encrypted data later on
        return `${EncryptionService.PREFIX_ENCRYPTED_CONTENT}${encrypted}`;
    }

    /**
     * Checks for valid parameters then prefixes the string data and encrypts it with AES and the private key.
     * Will throw errors if the provided parameters are illegal.
     * Returns a zip compressed Uint8Array
     *
     * @param data the data to be encrypted
     * @returns an Uint8Array which is the compressed version of the encrypted data
     * @throws Error if the service is not yet initialised
     */
    public encryptAndCompress(data: string): Uint8Array {
        if (!this.isInitialized) {
            throw new Error(
                `EncryptionService: encryptAndCompress: Trying to call EncryptionService before initialization!`,
            );
        }

        const encrypted = this.encrypt(data);
        return compressToUint8Array(encrypted);
    }

    public decrypt(data: string): string {
        if (!this.isInitialized) {
            throw new Error(`EncryptionService: decrypt: Trying to call EncryptionService before initialization!`);
        }

        if (!data || data.length < 1) {
            this.log.error(`EncryptionService: decrypt: The provided data was empty. Empty data cannot be decrypted.`);
            throw new Error(`EncryptionService: decrypt: The provided data was empty. Empty data cannot be decrypted.`);
        }

        if (data.startsWith(EncryptionService.PREFIX_ENCRYPTED_CONTENT)) {
            const decrypted = data.replace(EncryptionService.PREFIX_ENCRYPTED_CONTENT, '');
            return CryptoJS.AES.decrypt(decrypted, this.encryptionKey).toString(CryptoJS.enc.Utf8);
        }

        return data;
    }

    public decryptAndDecompress(data: Uint8Array): string {
        if (!this.isInitialized) {
            throw new Error(
                `EncryptionService: decryptAndDecompress: Trying to call EncryptionService before initialization!`,
            );
        }

        if (!data) {
            this.log.error(
                'EncryptionService: decryptAndDecompress: The provided data was empty. Empty data cannot be decrypted.',
            );
            throw new Error(
                'EncryptionService: decryptAndDecompress: The provided data was empty. Empty data cannot be decrypted.',
            );
        }
        const decompressed = decompressFromUint8Array(data);
        if (!decompressed) {
            throw new Error(`EncryptionService: decompressFromUint8Array: Unable to decompress data!`);
        }
        return this.decrypt(decompressed);
    }
}
